<template>
    <div class="customer-v3-container">
        <!-- Stats Header Cards -->
        <v-app class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Header -->
            <div class="d-flex flex-row align-items-center" :style="{ marginTop: '10px', marginBottom: normalizeSize(20, true) }">
                <h1
                    class="ppv2-text-black poppinsRegular uppercaseFirstLetter mb-0 title-header"
                    :style="{ fontSize: normalizeFontSize(26) }"
                >
                    {{ isEdit ? $t('customers.edit') : $t('customers.add') }} {{ $t('customers.title_single') }}
                </h1>
            </div>

            <div class="ppv2-w-full">
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Left Column -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <!-- Main Information -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div
                                class="ppv2-flex ppv2-items-center ppv2-justify-between"
                                :style="{ padding: normalizeSize(24, true) }"
                            >
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('form_customer_v2.main_information.title_infomation') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>

                            <div :style="{ padding: normalizeSize(24, true) }">
                                <div class="ppv2-flex ppv2-flex-wrap">
                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3 ppv2-flex ppv2-flex-col">
                                        <label
                                            class="poppinsMedium ppv2-text-black ppv2-mb-3"
                                            :style="{ fontSize: normalizeFontSize(15) }"
                                        >
                                            {{ $t('form_customer_v2.main_information.customer_type.label') }}
                                        </label>

                                        <div class="ppv2-flex ppv2-items-center !ppv2-mb-[20px]">
                                            <div class="toggle-button-container">
                                                <button
                                                    type="button"
                                                    :class="[
                                                        'toggle-option',
                                                        !form.is_professional ? 'poppinsMedium active' : 'poppinsRegular',
                                                    ]"
                                                    @click="form.is_professional = false"
                                                >
                                                    {{ $t('form_customer_v2.main_information.individual.label') }}
                                                </button>

                                                <button
                                                    type="button"
                                                    :class="[
                                                        'toggle-option',
                                                        form.is_professional ? 'poppinsMedium active' : 'poppinsRegular',
                                                    ]"
                                                    @click="form.is_professional = true"
                                                >
                                                    {{ $t('form_customer_v2.main_information.professional.label') }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <BaseSelect
                                            :label="$t('customerInfo.form.civility.label')"
                                            v-model="form.gender"
                                            :options="genderOptions"
                                            :placeholder="$t('customerInfo.form.civility.label')"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                            classContainerAdded="ppv2-min-h-[40px] !ppv2-mb-[20px]"
                                            fixed-dropdown
                                        />
                                    </div>
                                </div>

                                <div class="ppv2-flex ppv2-flex-wrap">
                                    <!-- Professional Fields -->
                                    <template v-if="form.is_professional">
                                        <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                            <CustomInput
                                                :label="$t('form_customer_v2.main_information.company_name.label')"
                                                v-model="form.social_reason"
                                                :required="form.is_professional"
                                                :placeholder="$t('form_customer_v2.main_information.company_name.placeholder')"
                                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                                :errorText="errors.social_reason"
                                                class-input-add="ppv2-min-h-[40px]"
                                            />
                                        </div>
                                        <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                            <CustomInput
                                                :label="$t('form_customer_v2.main_information.siret.label')"
                                                v-model="form.siret"
                                                :required="form.is_professional"
                                                :placeholder="$t('form_customer_v2.main_information.siret.placeholder')"
                                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                                :errorText="errors.siret"
                                                class-input-add="ppv2-min-h-[40px]"
                                            />
                                        </div>
                                    </template>

                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <CustomInput
                                            :label="$t('form_customer_v2.main_information.first_name.label')"
                                            v-model="form.first_name"
                                            :placeholder="$t('form_customer_v2.main_information.first_name.placeholder')"
                                            :required="true"
                                            :errorText="errors.first_name"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                            class-input-add="ppv2-min-h-[40px]"
                                        />
                                    </div>

                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <CustomInput
                                            :label="$t('form_customer_v2.main_information.last_name.label')"
                                            v-model="form.last_name"
                                            :placeholder="$t('form_customer_v2.main_information.last_name.placeholder')"
                                            :required="true"
                                            :errorText="errors.last_name"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                            class-input-add="ppv2-min-h-[40px]"
                                        />
                                    </div>
                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <CustomInput
                                            :label="$t('form_customer_v2.main_information.email.label')"
                                            v-model="form.email"
                                            type="email"
                                            :placeholder="$t('form_customer_v2.main_information.email.placeholder')"
                                            :required="true"
                                            :errorText="errors.email"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                            class-input-add="ppv2-min-h-[40px]"
                                        />
                                    </div>
                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <!-- Indicatif et numéro sont saisis — et stockés — séparément.
                                             Coller un numéro international ne casse rien :
                                             `onPhoneInput` détache l'indicatif vers le sélecteur. -->
                                        <div class="phone-group">
                                            <select
                                                v-model="form.phone_country_code"
                                                class="phone-code poppinsMedium"
                                                :aria-label="$t('form_customer_v2.main_information.phone.label')"
                                            >
                                                <option v-for="code in dialCodes" :key="code" :value="code">
                                                    {{ code }}
                                                </option>
                                            </select>
                                            <CustomInput
                                                :label="$t('form_customer_v2.main_information.phone.label')"
                                                v-model="form.phone_number"
                                                :required="true"
                                                :errorText="errors.phone_number"
                                                :placeholder="$t('form_customer_v2.main_information.phone.placeholder')"
                                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                                class-input-add="ppv2-min-h-[40px]"
                                                class="phone-number"
                                                @input="onPhoneInput"
                                            />
                                        </div>
                                    </div>

                                    <template v-if="form.is_professional">
                                        <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                            <CustomInput
                                                :label="$t('form_customer_v2.main_information.function.label')"
                                                v-model="form.fonction"
                                                :placeholder="$t('form_customer_v2.main_information.function.placeholder')"
                                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                                class-input-add="ppv2-min-h-[40px]"
                                            />
                                        </div>
                                        <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                            <CustomInput
                                                :label="$t('form_customer_v2.main_information.vat.label')"
                                                v-model="form.tva"
                                                :placeholder="$t('form_customer_v2.main_information.vat.placeholder')"
                                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                                class-input-add="ppv2-min-h-[40px]"
                                            />
                                        </div>
                                        <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                            <CustomInput
                                                :label="$t('form_customer_v2.main_information.fix_phone.label')"
                                                v-model="form.phone_fixed"
                                                :placeholder="$t('form_customer_v2.main_information.fix_phone.placeholder')"
                                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                                class-input-add="ppv2-min-h-[40px]"
                                            />
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </v-card>

                        <!-- Informations complémentaires -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div
                                class="ppv2-flex ppv2-items-center ppv2-justify-between"
                                :style="{ padding: normalizeSize(24, true) }"
                            >
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('customerInfo.titles.additional_information') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>

                            <div :style="{ padding: normalizeSize(24, true) }">
                                <div class="ppv2-flex ppv2-flex-wrap">
                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <BaseDateInput
                                            :label="$t('form_customer_v2.main_information.date_of_birth.label')"
                                            v-model="form.birthday"
                                            :placeholder="$t('form_customer_v2.main_information.date_of_birth.placeholder')"
                                            :customSizeLabel="15"
                                            input-class="!ppv2-min-h-[40px]"
                                            classContainerAdd="!ppv2-mb-[20px]"
                                        />
                                    </div>
                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <BaseSelect
                                            :label="$t('customerInfo.form.source_of_acquisition.label')"
                                            v-model="form.origin_id"
                                            :options="originOptions"
                                            :placeholder="$t('customerInfo.form.source_of_acquisition.placeholder')"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                            classContainerAdded="!ppv2-mb-[20px]"
                                            fixed-dropdown
                                        />
                                    </div>
                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3 address-block">
                                        <CustomInput
                                            :label="$t('customerInfo.form.address.label')"
                                            v-model="form.street"
                                            :placeholder="$t('customerInfo.form.address.label')"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                            :required="false"
                                            :errorText="errors.street"
                                            class-input-add="ppv2-min-h-[40px]"
                                            @input="onAddressInput(form.street)"
                                        />

                                        <p v-if="addressLoading" class="address-hint">
                                            {{ $t('devisV3.clientSelector.addressSearching') }}
                                        </p>

                                        <!-- Le repli manuel n'a rien à ajouter ici : les champs
                                             ville, code postal et pays existent déjà juste en
                                             dessous. Un message suffit à les désigner. -->
                                        <p v-else-if="addressFallback" class="address-hint">
                                            {{ $t('devisV3.clientSelector.addressNoResult') }}
                                        </p>

                                        <ul v-if="addressSuggestions.length > 0" class="address-results">
                                            <li
                                                v-for="suggestion in addressSuggestions"
                                                :key="suggestion.place_id || suggestion.description"
                                                class="address-result"
                                                @click="applyAddressSuggestion(suggestion)"
                                            >
                                                <span class="address-main poppinsMedium">{{
                                                    suggestion.main_text || suggestion.description
                                                }}</span>
                                                <span v-if="suggestion.secondary_text" class="address-secondary">{{
                                                    suggestion.secondary_text
                                                }}</span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <CustomInput
                                            :label="$t('form_customer_v2.addresses.city.label')"
                                            v-model="form.city"
                                            :placeholder="$t('form_customer_v2.addresses.city.placeholder')"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                            :required="false"
                                            :errorText="errors.city"
                                            class-input-add="ppv2-min-h-[40px]"
                                        />
                                    </div>
                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <CustomInput
                                            :label="$t('form_customer_v2.addresses.zip_code.label')"
                                            v-model="form.postal_code"
                                            :placeholder="$t('form_customer_v2.addresses.zip_code.placeholder')"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                            :required="false"
                                            :errorText="errors.postal_code"
                                            class-input-add="ppv2-min-h-[40px]"
                                        />
                                    </div>
                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <CustomInput
                                            :label="$t('form_customer_v2.addresses.country.label')"
                                            v-model="form.country"
                                            :required="false"
                                            :placeholder="$t('form_customer_v2.addresses.country.placeholder')"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                            class-input-add="ppv2-min-h-[40px]"
                                        />
                                    </div>
                                    <div class="ppv2-w-full md:ppv2-w-full ppv2-px-3">
                                        <BaseTextarea
                                            label="Note"
                                            v-model="form.internal_note"
                                            :required="false"
                                            placeholder="Note"
                                            :useEditor="false"
                                            :rows="3"
                                            :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                            customLabelClass="poppinsMedium ppv2-text-black"
                                        />
                                    </div>
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <!-- Right Column -->
                    <div class="ppv2-w-full md:ppv2-w-[35%]">
                        <!-- Status & Metrics Card -->
                        <div
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                            :style="{ padding: normalizeSize(24, true) }"
                        >
                            <!-- Status Switches -->
                            <div class="ppv2-flex ppv2-flex-col ppv2-space-y-4 ppv2-mb-4">
                                <BaseSwitch
                                    :label="form.active ? $t('common.enabled') : $t('common.disabled')"
                                    v-model="form.active"
                                    :boxed="true"
                                    :variant="form.active ? 'success' : 'gray'"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                />
                                <BaseSwitch
                                    :label="$t('form_customer_v2.main_information.subscribed_to_communications')"
                                    v-model="form.is_newsletter_subscribed"
                                    :boxed="true"
                                    variant="primary"
                                    class="ppv2-w-full"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                />
                            </div>

                            <!-- Metrics List -->
                            <div class="ppv2-space-y-3">
                                <MetricsList :metrics="metrics" />
                            </div>
                        </div>

                        <!-- Groups Card -->
                        <div
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div
                                class="ppv2-flex ppv2-items-center ppv2-justify-between"
                                :style="{ padding: normalizeSize(24, true) }"
                            >
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('customerInfo.titles.groups') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>

                            <div class="ppv2-space-y-3" :style="{ padding: normalizeSize(24, true) }">
                                <div class="ppv2-w-full">
                                    <BaseSelect
                                        :label="$t('form_customer_v2.main_information.customer_group.label')"
                                        v-model="form.customer_group_id"
                                        :options="groupOptions"
                                        :placeholder="$t('form_customer_v2.main_information.customer_group.placeholder')"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        fixed-dropdown
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Bottom Actions -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <div
                            class="ppv2-flex ppv2-justify-between ppv2-items-center"
                            :style="{ marginTop: normalizeSize(24), marginBottom: normalizeSize(40, true) }"
                        >
                            <BackButton type="button" @click="cancel" />
                            <BaseButton
                                variant="primary"
                                :loading="isSaving"
                                @click="save"
                                :customHeight="48"
                                customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3]"
                            >
                                {{ $t('common.validate') }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </div>
        </v-app>

        <!-- Exit Dialog -->
        <CustomDialog v-model="showExitPopup" :defaultTitle="$t('experience.titleClosePopup')" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('experience.descriptionClosePopup') }}
                </p>
            </template>
            <template #actions>
                <BaseButton variant="outline" @click="exit">{{ $t('experience.btnLeave') }}</BaseButton>
                <BaseButton variant="primary" @click="showExitPopup = false">{{ $t('experience.btnStay') }} </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import addressAutocompleteMixin from '../../../mixins/addressAutocompleteMixin'
import CustomInput from '../../Inputs/CustomInput.vue'
import BackButton from '../../Shared/BackButton.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseTextarea from '../../Shared/BaseTextarea.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import BaseDateInput from '../../Shared/BaseDateInput.vue'
import normalizeSize from '../../../utils/normalizeSize'
import axios from 'axios'
import MetricsList from './MetricsList.vue'
import { Star, Wallet, CalendarCheck, CircleDollarSign, History, Gift, Calendar, Check } from 'lucide-vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'CustomerForm',
    components: {
        CustomInput,
        BackButton,
        BaseButton,
        BaseTextarea,
        BaseSelect,
        BaseSwitch,
        CustomDialog,
        BaseDateInput,
        MetricsList,
        Star,
        Wallet,
        CalendarCheck,
        CircleDollarSign,
        History,
        Gift,
        Calendar,
        Check,
    },
    props: {
        customerUser: { type: Object, default: () => null },
        customerGroups: { type: Array, default: () => [] },
        origins: { type: Array, default: () => [] },
        genders: { type: Array, default: () => [] },
        userId: { type: [Number, String], default: null },
        walletBalance: { type: [Number, String], default: null },
        spentIn: { type: [Number, String], default: null },
        orderTotal: { type: [Number, String], default: 0 },
        treatmentTotal: { type: [Number, String], default: 0 },
        voucherTotal: { type: [Number, String], default: 0 },
        isModal: { type: Boolean, default: false },
        token: { type: String, default: '' },
    },
    // Suggestions d'adresse : même logique que le formulaire du devis, factorisée.
    mixins: [addressAutocompleteMixin],
    data() {
        return {
            // Indicatifs proposés. La liste reste courte — les pays d'où viennent les
            // clients — et le champ accepte de toute façon un numéro collé avec le sien.
            dialCodes: ['+33', '+32', '+41', '+352', '+49', '+34', '+39', '+44', '+216', '+212', '+213', '+1'],
            isSaving: false,
            showExitPopup: false,
            form: {
                id: null,
                is_professional: false,
                social_reason: '',
                siret: '',
                last_name: '',
                first_name: '',
                email: '',
                phone_number: '',
                phone_country_code: '+33',
                phone_fixed: '',
                fonction: '',
                tva: '',
                customer_group_id: null,
                origin_id: null,
                birthday: '',
                gender: null,
                country: 'France',
                city: '',
                postal_code: '',
                street: '',
                active: true,
                block_online_booking: false,
                block_payment_by_gift_voucher: false,
                allow_receiving_emails: true,
                allow_receiving_sms_messages: true,
                is_newsletter_subscribed: true,
                internal_note: '',
            },
            errors: {},
            initialForm: null,
        }
    },
    computed: {
        isEdit() {
            return !!(this.customerUser && this.customerUser.id)
        },
        groupOptions() {
            return this.customerGroups.map((g) => ({ label: g.name, value: g.id }))
        },
        originOptions() {
            return this.origins.map((o) => ({ label: o.name, value: o.id }))
        },
        genderOptions() {
            return this.genders.map((g) => ({ label: g, value: g }))
        },
        metrics() {
            const customer = this.customerUser?.customer || {}
            return [
                { label: this.$t('customerInfo.metrics.loyalty_points'), value: customer.loyalty_points || 0, icon: Star },
                {
                    label: this.$t('customerInfo.metrics.wallet'),
                    value: this.walletBalance || '0,00' + this.$symbolCurrency,
                    icon: Wallet,
                },
                {
                    label: this.$t('customerInfo.metrics.reservations'),
                    value: this.orderTotal,
                    icon: CalendarCheck,
                },
                {
                    label: this.$t('customerInfo.metrics.total_spent'),
                    value:
                        new Intl.NumberFormat(this.$currencyLocale?.replace('_', '-') || 'fr-FR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }).format(this.spentIn ?? 0) + this.$symbolCurrency,
                    icon: CircleDollarSign,
                },
                { label: this.$t('customerInfo.metrics.packages_and_subscriptions'), value: this.treatmentTotal, icon: History },
                { label: this.$t('customerInfo.metrics.vouchers'), value: this.voucherTotal, icon: Gift },
                {
                    label: this.$t('customerInfo.metrics.date_created'),
                    value: this.formatDate(this.customerUser?.created_at),
                    icon: Calendar,
                },
            ]
        },
    },
    created() {
        if (this.customerUser) {
            this.initForm()
        }
        this.initialForm = JSON.stringify(this.form)
    },
    mounted() {
        // Remove white background from parent container
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('customer-v3-no-bg')
        }
    },
    beforeDestroy() {
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.remove('customer-v3-no-bg')
        }
    },
    methods: {
        /**
         * Saisie ou COLLAGE dans le champ numéro.
         *
         * Un numéro collé avec son indicatif — « +32 470… », « 0041 79… » — est scindé :
         * l'indicatif rejoint le sélecteur, le reste le champ. Sans ça, le back recevrait un
         * national commençant par « + » et l'indicatif affiché deviendrait faux.
         */
        onPhoneInput() {
            const raw = String(this.form.phone_number || '').replace(/[\s.\-()]/g, '')

            // « 00 » est la forme longue de « + ». On la ramène à « + » avant de comparer.
            const international = raw.startsWith('+') ? raw : raw.startsWith('00') ? '+' + raw.slice(2) : null

            if (!international) return

            // Les indicatifs les plus longs d'abord : « +33 » ne doit pas l'emporter sur « +352 ».
            const match = [...this.dialCodes].sort((a, b) => b.length - a.length).find((code) => international.startsWith(code))

            if (!match) return

            this.form.phone_country_code = match
            this.form.phone_number = international.slice(match.length)
        },
        /** Indicatif porté par un numéro déjà enregistré, « +33 » à défaut. */
        deduceDialCode(phoneNumber) {
            const raw = String(phoneNumber || '').replace(/[\s.\-()]/g, '')

            return [...this.dialCodes].sort((a, b) => b.length - a.length).find((code) => raw.startsWith(code)) || '+33'
        },
        /** Numéro débarrassé de son indicatif, pour le champ de saisie. */
        nationalPart(phoneNumber) {
            const raw = String(phoneNumber || '').replace(/[\s.\-()]/g, '')

            if (!raw) return ''

            const code = this.deduceDialCode(raw)

            return raw.startsWith(code) ? raw.slice(code.length) : raw
        },

        /**
         * Une suggestion choisie recopie SON TEXTE dans le champ rue — rien d'autre.
         *
         * L'adresse reste une simple chaîne : ni identifiant Google, ni découpage. Ville, code
         * postal et pays gardent leurs propres champs, que l'utilisateur remplit s'il le veut.
         */
        applyAddressSuggestion(suggestion) {
            this.form.street = suggestion.description
            this.clearAddressSuggestions()
            this.addressFallback = false
        },
        normalizeFontSize,
        normalizeSize,
        initForm() {
            const customer = this.customerUser.customer || {}
            const address =
                this.customerUser.addresses && this.customerUser.addresses.length > 0 ? this.customerUser.addresses[0] : {}
            const rgpd = customer.rgpd || {}

            const notes = this.customerUser.notes && this.customerUser.notes.length > 0 ? this.customerUser.notes[0] : {}

            console.log(this.customerUser.notes)

            this.form = {
                id: this.customerUser.id,
                is_professional: customer.customer_type == 1,
                social_reason: customer.social_reason || '',
                siret: customer.siret || '',
                tva: customer.tva || '',
                last_name: this.customerUser.last_name || '',
                first_name: this.customerUser.first_name || '',
                email: this.customerUser.email || '',
                phone_number: this.nationalPart(this.customerUser.phone_number),
                phone_country_code: this.deduceDialCode(this.customerUser.phone_number),
                phone_fixed: customer.phone_fixed || '',
                fonction: customer.fonction || '',
                tva: customer.tva || '',
                customer_group_id: customer.customer_group,
                origin_id: customer.origin_id,
                birthday: this.customerUser.birthday ? this.customerUser.birthday.split(' ')[0] : '',
                gender: this.customerUser.gender,
                country: address.country || 'France',
                city: address.city || '',
                postal_code: address.postal_code || '',
                street: address.street || '',
                active: this.customerUser.status_id === 1,
                block_online_booking: !!customer.block_online_booking,
                block_payment_by_gift_voucher: !!customer.block_payment_by_gift_voucher,
                allow_receiving_emails: rgpd.receive_email === 1,
                allow_receiving_sms_messages: rgpd.receive_sms === 1,
                is_newsletter_subscribed: !!this.customerUser.is_newsletter_subscribed,
                internal_note: notes.content || '', // Notes are usually separate, but for the form we might want a single field or handle it differently
            }
        },
        exit() {
            if (JSON.stringify(this.form) !== this.initialForm) {
                this.showExitPopup = true
            } else {
                this.$emit('success', this.form)
            }
        },
        cancel() {
            window.location.href = '/back-office/v3/customer'
        },
        async save() {
            this.errors = {}
            this.isSaving = true
            try {
                const url = this.isEdit ? `/api/v3/customer/${this.form.id}` : '/api/v3/customer/store'
                const method = this.isEdit ? 'put' : 'post'

                // Prepare data for backend (this might need to match existing store logic)
                const data = {
                    ...this.form,
                    customer_type: this.form.is_professional ? 1 : 0,
                    status_id: this.form.active ? 1 : 2,
                }

                const response = await axios[method](url, data, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.$toast.success(this.$t('common.success'))
                this.initialForm = JSON.stringify(this.form)
                if (this.isModal) {
                    this.$emit('success', this.form)
                } else {
                    this.exit()
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.data && error.response.data.errors) {
                    this.errors = error.response.data.errors
                }
            } finally {
                this.isSaving = false
            }
        },
        formatDate(dateStr) {
            console.log(dateStr)
            if (!dateStr) return '--'

            // Split date and time
            const [datePart] = dateStr.split(' ')
            const [day, month, year] = datePart.split('-')

            // Create date (month is 0-based in JS)
            const date = new Date(year, month - 1, day)

            return date.toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            })
        },
    },
}
</script>

<style scoped lang="scss">
/* Indicatif et numéro se lisent comme UN seul champ : mêmes bordure, rayon et hauteur que
   `CustomInput`, pour que le sélecteur natif ne paraisse pas rapporté à côté. */
.phone-group {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    /* La marge basse reprise du champ, pour garder le rythme vertical du formulaire. */
    margin-bottom: 20px;

    /* `CustomInput` porte lui-même un `margin-bottom: 20px`. Un alignement par le bas cale
       le sélecteur sur la MARGE du champ, donc 20 px trop bas. On l'annule ici, dans le
       groupe, plutôt que de toucher au composant partagé. */
    ::v-deep .custom-input-wrapper {
        margin-bottom: 0;
    }

    .phone-number {
        flex: 1 1 auto;
        min-width: 0;
    }
}

.phone-code {
    flex: 0 0 auto;
    /* Valeurs reprises de `CustomInput`. */
    height: 40px;
    border: 1px solid #9c9af880;
    border-radius: 12px;
    padding: 5.5px 10px;
    background: #fff;
    color: #1d1d1b;
    font-size: 14px;
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s ease;

    &:hover,
    &:focus {
        border-color: #9c9af8b2;
    }
}

/* Suggestions d'adresse. La liste se déplie DANS le flux, sous le champ : un menu flottant
   serait masqué par le défilement du formulaire. */
.address-block {
    position: relative;
}

.address-hint {
    margin-top: 4px;
    color: #1d1d1b;
    opacity: 0.5;
    font-size: 12px;
}

.address-results {
    margin-top: 6px;
    max-height: 190px;
    overflow-y: auto;
    border: 1px solid #9c9af880;
    border-radius: 12px;
    background: #fff;
}

.address-result {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 9px 12px;
    cursor: pointer;
    transition: background 0.15s ease;

    & + .address-result {
        border-top: 1px solid #f4f6f9;
    }

    &:hover {
        background: #f7f4fb;
    }
}

.address-main {
    color: #1d1d1b;
    font-size: 13px;
}

.address-secondary {
    color: #1d1d1b;
    opacity: 0.55;
    font-size: 11.5px;
}

.customer-v3-container {
    padding: 0;
}

.title-header {
    letter-spacing: -0.02em;
}

:deep(.v-application--wrap) {
    min-height: auto;
}

:deep(.v-card) {
    border-radius: 10px !important;
}

.toggle-button-container {
    display: flex;
    background-color: #7d2dbd1a;
    border-radius: 12px;
    padding: 0;
    width: 100%;
    max-width: 400px;
}

.toggle-option {
    flex: 1;
    padding: 9px 20px;
    border-radius: 10px;
    border: none;
    background-color: transparent;
    color: #94a3b8;
    font-size: clamp(12px, 0.78vw, 16px);
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    outline: none;
    text-align: center;
    white-space: nowrap;

    &:hover:not(.active) {
        color: #7d2dbd;
    }

    &.active {
        background-color: #7d2dbd;
        color: white;
        box-shadow: 0 4px 12px rgba(125, 45, 189, 0.25);
    }
}
</style>

<style lang="scss">
#main-container.customer-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}
</style>
